<!-- 仮設道路・平場名のコンポーネント -->
<template>
	<div class="obj-name ml-2">
		<!-- ファイル名表示 -->
		<ListItemLabel v-if="!isRenamingMode" :text="obj?.name ?? ''" />
		<!-- ファイル名編集 -->
		<div class="obj-name-input" v-else>
			<VeeValidateForm :initial-values="{ name: obj.name }" v-slot="{ meta }">
				<VeeValidateField name="name" :rules="nameRules" v-slot="{ field, errorMessage }">
					<ErrorTooltip placement="bottom" :shown="!meta.valid" :triggers="[]">
						<v-text-field v-model="name" v-bind="field" type="text" :error="!meta.valid" color="primary"
							class="ma-0 pa-0" hide-details="" variant="outlined" autofocus style="font-size: 14px"
							:loading="isLoading" @blur="blur(meta.valid)" @keydown.enter.prevent="onEnterKeydown($event, meta.valid)"
							@keyup.escape="cancelUpdateFileName">
						</v-text-field>
						<template #popper>
							<span>{{ errorMessage }}</span>
						</template>
					</ErrorTooltip>
				</VeeValidateField>
			</VeeValidateForm>
		</div>
	</div>
</template>

<script>
import { isImeComposingKeyEvent } from "@/utils/imeComposition";
import ListItemLabel from "@/components/Project/ListItemLabel.vue";
import ErrorTooltip from "@/components/common/ErrorTooltip";
import objUpdateMixin from "@/mixins/objUpdateMixin.js";
import * as lockUtils from "@/utils/lock-utils";
import { notifyError, notifySuccess } from "@/utils/notificationUtils";
import {
	Field as VeeValidateField,
	Form as VeeValidateForm,
} from "vee-validate";
import { mapMutations } from "vuex";

export default {
	mixins: [objUpdateMixin],
	components: {
		VeeValidateForm,
		VeeValidateField,
		ErrorTooltip,
		ListItemLabel,
	},
	props: {
		objId: String,
		isRenamingMode: Boolean,
	},
	data() {
		return {
			name: "", // オリジナルの名前（obj.name）とは別に変更後の名前を保持する
			hasUpdatedError: false, // 前回のAPIリクエストのエラー有無
			nameRules: [
				(value) => {
					if (value) return true;
					return this.$t("REQUIRED_ERROR");
				},
				(value) => {
					if (value?.length <= 100) return true;
					return this.$t("MAX_LENGTH_ERROR");
				},
			],
		};
	},
	computed: {
		isLoading: {
			get() {
				return this.$store.state.isLoading;
			},
			set(value) {
				this.$store.dispatch("updateIsLoading", value);
			},
		},
		isEditOnProgress() {
			return this.$store.state.object.objectUnderEdit?.id === this.obj.id;
		},
	},
	watch: {
		name() {
			// 変更があればサーバーエラー解除
			if (this.hasUpdatedError) this.hasUpdatedError = false;
		},
	},
	methods: {
		...mapMutations(["set_snackbar"]),
		async blur(valid) {
			// 前回のリクエストが終わっていない場合はリクエストを送らない（多重リクエスト防止）
			if (this.isLoading) return;

			// 前回のリクエストでエラーが発生していた場合はフォーカスアウト時にリクエスト送信しない
			if (this.hasUpdatedError) {
				this.cancelUpdateFileName();
				return;
			}
			// バリデーションエラーがある場合編集キャンセル
			if (!valid) {
				this.cancelUpdateFileName();
				return;
			}
			// ファイル名に変更がなければリクエストを送らない
			if (this.name === this.obj.name) {
				this.endNameEditing();
				return;
			}
			// スピナー表示開始
			await this.$store.dispatch("executeWithSpinner", async () => {
				// 編集中でない場合ロックを取得する。失敗した場合処理終了
				if (!this.isEditOnProgress) {
					const lockAcquired = await lockUtils.lock(
						this.$route.query.siteId,
						this.obj.id,
					);
					if (!lockAcquired) {
						this.cancelUpdateFileName();
						return;
					}
				}

				// リクエスト成功の場合は編集モード終了、失敗の場合は変更を戻す
				const status = await this.updateFileName();
				if (status) {
					this.endNameEditing();
				} else {
					this.cancelUpdateFileName();
				}
				// ロックを解放する
				if (!this.isEditOnProgress) {
					lockUtils.releaseLock(this.obj.id);
				}
			});
		},
		/** Confirm the name on Enter, ignoring the one that confirms IME composition. */
		onEnterKeydown(e, valid) {
			if (isImeComposingKeyEvent(e)) return;
			this.enter(valid);
		},
		async enter(valid) {
			// 前回のリクエストが終わっていない場合はリクエストを送らない（多重リクエスト防止）
			if (this.isLoading) return;

			// バリデーションエラーがある場合Enterキーを押しても確定させない
			if (!valid) {
				return;
			}
			// ファイル名に変更がなければリクエストを送らない
			if (this.name === this.obj.name) {
				this.endNameEditing();
				return;
			}

			// スピナー表示開始
			await this.$store.dispatch("executeWithSpinner", async () => {
				// 編集中でない場合ロックを取得する。失敗した場合処理終了
				if (!this.isEditOnProgress) {
					const lockAcquired = await lockUtils.lock(
						this.$route.query.siteId,
						this.obj.id,
					);
					if (!lockAcquired) {
						// フォーカスアウト（blur）時にリクエストを送らないようにエラーフラグを更新
						this.hasUpdatedError = true;
						return;
					}
				}

				// リクエスト成功の場合は編集モード終了
				const status = await this.updateFileName();
				if (status) {
					this.endNameEditing();
				}
				// ロックを解放する
				if (!this.isEditOnProgress) {
					lockUtils.releaseLock(this.obj.id);
				}
			});
		},
		// ファイル名変更
		async updateFileName() {
			this.obj.name = this.name;
			const body = {
				siteId: this.$route.query.siteId,
				objJSON: {},
				objProps: await this.obj.toJSON(),
			};
			const status = await this.request(body);
			return status;
		},
		// ファイル名変更キャンセル
		cancelUpdateFileName() {
			this.name = this.obj.name;
			this.endNameEditing();
		},
		// ファイル名編集終了
		endNameEditing() {
			this.hasUpdatedError = false;
			this.obj.name = this.name;
			this.$emit("end-rename");
		},
		async request(body) {
			const successMessage = `${this.$t("UPDATE_DATA")} ${this.$t("successful")}`;
			const errorMessage = `${this.$t("UPDATE_DATA")} ${this.$t("failed")}`;
			try {
				this.isLoading = true;
				const status = await this.$store.dispatch("object/updateName", {
					obj: this.obj,
					objProps: body.objProps,
				});
				if (status === 200) {
					notifySuccess({ $store: this.$store, $t: this.$t }, successMessage);
					return status;
				} else {
					throw new Error(errorMessage);
				}
			} catch {
				this.hasUpdatedError = true;
				notifyError({ $store: this.$store, $t: this.$t }, errorMessage);
				return "";
			}
		},
	},
	created() {
		this.name = this.obj?.name;
	},
};
</script>

<style lang="scss" scoped>
.obj-name {
	flex: 1;
	min-width: 0;
	display: flex;
	align-items: center;

	:deep(input.v-field__input) {
		padding: 0 6px;
		min-height: 0px;
	}
}

.obj-name-input {
	width: 100%;
}
</style>